import { Box, UserAvatar, Typography, Paper, ReplayIcon, CircularProgress, IconButton, ArrowDownwardIcon } from "src/components"
import { useIsMobile } from "src/hooks"
import { format } from "date-fns"
import { useAuth, markChatAsRead } from "src/utils"
import { useEffect, useRef, useState } from "react"
import { useMessagingStore } from "src/stores"

const MESSAGE_INCREMENT = 20

const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

const formatDate = (timestamp) => {
  const date = new Date(timestamp)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) return "Today"
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday"
  return format(date, "MMM dd, yyyy")
}

export default function MessageList({ chatId, messages = [], onLoadOlderMessages, onRetry, isAIChat }) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const scrollRef = useRef(null);
  const lastMarkedReadRef = useRef(null);

  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const setLastRead = useMessagingStore((s) => s.setLastRead);

  const markAsReadIfNeeded = async () => {
    if (!messages.length) return;

    const lastMessage = messages[messages.length - 1]; // latest message
    if (lastMarkedReadRef.current === lastMessage.id) return; // already marked

    await markChatAsRead(chatId);
    setLastRead(chatId, new Date().toISOString());

    lastMarkedReadRef.current = lastMessage.id;
  };

  const loadOlderMessages = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);

    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight || 0;

    try {
      const res = await onLoadOlderMessages?.();
      if (res?.length < MESSAGE_INCREMENT) setHasMore(false);

      requestAnimationFrame(() => {
        if (el) {
          const newHeight = el.scrollHeight;
          el.scrollTop = newHeight - prevHeight; 
        }
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = async () => {
    const el = scrollRef.current;
    if (!el) return;

    const threshold = 50;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setIsUserAtBottom(isAtBottom);

    // Only mark as read if user is at bottom
    if (isAtBottom) {
      setShowScrollToBottom(false);
      await markAsReadIfNeeded();
    }

    // Load older messages if scrolled to top
    if (el.scrollTop <= threshold) {
      await loadOlderMessages();
    }
  };

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (isUserAtBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" })
    } else {
      setShowScrollToBottom(true);
    }
  }, [messages, isUserAtBottom])

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;

    const threshold = 50;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    const contentFits = el.scrollHeight <= el.clientHeight;

    if (isAtBottom || contentFits) {
      markAsReadIfNeeded();
    }
  }, [messages, chatId]);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setShowScrollToBottom(false);
  };

  if (isAIChat) {
    return (
      <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#fff',
        zIndex: 10
      }}>
        <Typography variant="h2">Coming Soon</Typography>
        <Typography variant="body2" sx={{textAlign: 'center', width: '70%', mt: 1}}>A custom GPT trained on Pan American Highway data to specifically help travellers with their problems, from border information to country attractions.</Typography>
      </Box>
    );
  }

  return (<>
    <Box
      ref={scrollRef}
      onScroll={handleScroll}
      className="scrollBox"
      sx={{
        position: "relative",
        flexGrow: 1,
        overflowY: "auto",
        px: isMobile ? 2 : 10,
        pb: 4,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%"
      }}
    >
      {loadingMore && (
        <CircularProgress size={18} sx={{ alignSelf: "center", mt: 3 }} />
      )}

      {messages.map((message, index) => {
        const showDateDivider =
          index === 0 ||
          formatDate(messages[index - 1].timestamp) !==
            formatDate(message.timestamp)

        const sentByCurrentUser = message.senderId === user?.id
        const status = message.status

        return (
          <Box key={message.id || message.tempId || index}>
            {showDateDivider && (
              <Box sx={{ textAlign: "center", my: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  {formatDate(message.timestamp)}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: sentByCurrentUser ? "row-reverse" : "row",
                alignItems: "flex-start",
                gap: 1.5,
                opacity: status === "sending" ? 0.7 : 1,
              }}
            >
              {sentByCurrentUser ? (
                <UserAvatar user={user} size={32} />
              ) : (
                <UserAvatar
                  user={{
                    id: message.senderId,
                    name: message.senderName,
                    profileImageUrl: message.senderImageUrl,
                  }}
                  size={32}
                />
              )}

              <Box
                sx={{
                  maxWidth: "70%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: sentByCurrentUser ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                    flexDirection: sentByCurrentUser ? "row-reverse" : "row",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="textPrimary"
                  >
                    {sentByCurrentUser ? user.name : message.senderName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatTime(message.timestamp)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.5,
                      color:
                        status === "failed"
                          ? "text.primary"
                          : sentByCurrentUser
                          ? "white"
                          : "text.primary",
                      borderRadius: 2,
                      border: "1px solid",
                      backgroundColor:
                        status === "failed"
                          ? "#fdecea"
                          : sentByCurrentUser
                          ? "primary.main"
                          : "#f8f9fa",
                      borderColor:
                        status === "failed"
                          ? "error.main"
                          : sentByCurrentUser
                          ? "primary.main"
                          : "#e1e5e9",
                    }}
                  >
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      {message.content.text}
                    </Typography>
                  </Paper>

                  {sentByCurrentUser && status === "sending" && (
                    <CircularProgress size={16} thickness={5} />
                  )}

                  {sentByCurrentUser && status === "failed" && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRetry?.(message)}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )
      })}

      {showScrollToBottom && (
        <IconButton
          onClick={scrollToBottom}
          size="small"
          disableRipple
          sx={{
            position: "fixed",
            bottom: isMobile ? 180 : 150,
            left: isMobile ? "50%" : "60%",
            transform: isMobile ? "translateX(-50%)" : "translateX(-60%)",
            zIndex: 10000,
            bgcolor: "#f8f9fa",
            "&:hover": { bgcolor: "primary" },
          }}
        >
          <ArrowDownwardIcon />
        </IconButton>
      )}

    </Box>
  </>)
}
